"use client";

import { useState } from "react";
import { Cookie, Settings, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEEADetection } from "@/hooks/use-eea-detection";
import { useConsent } from "@/hooks/use-consent";

export function CookieConsent() {
  const t = useTranslations();
  const { isEEA, isLoading } = useEEADetection();
  const { hasConsented, isExpired, updateConsent, acceptAll, rejectAll } =
    useConsent();

  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [adsEnabled, setAdsEnabled] = useState(true);

  if (isLoading) return null;
  if (!isEEA) return null;
  if (hasConsented && !isExpired) return null;

  const handleSavePreferences = () => {
    updateConsent(analyticsEnabled, adsEnabled);
    setShowPreferences(false);
  };

  if (showPreferences) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/90 backdrop-blur-md"
        data-testid="cookie-preferences-overlay"
      >
        <div
          className="bg-card border border-border card-elevated rounded-3xl p-6 max-w-md w-full flex flex-col space-y-5 animate-scale-in"
          data-testid="cookie-preferences-card"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold text-foreground">
              {t("cookie_title")}
            </h2>
            <button
              onClick={() => setShowPreferences(false)}
              className="p-2 rounded-xl hover:bg-secondary transition-colors cursor-pointer"
              aria-label="Close preferences"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 p-4 rounded-2xl bg-secondary/50">
              <div className="flex-1">
                <p className="font-semibold text-foreground text-sm">
                  {t("cookie_essential_label")}
                </p>
                <p className="text-muted-foreground text-xs mt-1">
                  {t("cookie_essential_desc")}
                </p>
              </div>
              <div className="flex items-center justify-center w-12 h-6 rounded-full bg-primary/20">
                <span className="text-xs text-primary font-medium">ON</span>
              </div>
            </div>

            <div className="flex items-start justify-between gap-4 p-4 rounded-2xl bg-secondary/50">
              <div className="flex-1">
                <p className="font-semibold text-foreground text-sm">
                  {t("cookie_analytics_label")}
                </p>
                <p className="text-muted-foreground text-xs mt-1">
                  {t("cookie_analytics_desc")}
                </p>
              </div>
              <button
                onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                  analyticsEnabled ? "bg-primary" : "bg-muted"
                }`}
                aria-pressed={analyticsEnabled}
                aria-label="Toggle analytics cookies"
              >
                <span
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    analyticsEnabled
                      ? "translate-x-7"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-start justify-between gap-4 p-4 rounded-2xl bg-secondary/50">
              <div className="flex-1">
                <p className="font-semibold text-foreground text-sm">
                  {t("cookie_ads_label")}
                </p>
                <p className="text-muted-foreground text-xs mt-1">
                  {t("cookie_ads_desc")}
                </p>
              </div>
              <button
                onClick={() => setAdsEnabled(!adsEnabled)}
                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                  adsEnabled ? "bg-primary" : "bg-muted"
                }`}
                aria-pressed={adsEnabled}
                aria-label="Toggle advertising cookies"
              >
                <span
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    adsEnabled ? "translate-x-7" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={rejectAll}
              className="flex-1 px-4 py-3 rounded-xl border border-border text-foreground hover:bg-secondary font-semibold transition-all text-sm cursor-pointer"
            >
              {t("cookie_reject_all")}
            </button>
            <button
              onClick={handleSavePreferences}
              className="flex-1 px-4 py-3 rounded-xl btn-primary text-primary-foreground font-semibold text-sm cursor-pointer"
            >
              {t("cookie_save")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/90 backdrop-blur-md"
      data-testid="cookie-consent-overlay"
    >
      <div
        className="bg-card border border-border card-elevated rounded-3xl p-8 max-w-sm w-full flex flex-col items-center text-center space-y-5 animate-scale-in"
        data-testid="cookie-consent-card"
      >
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
          <Cookie className="w-7 h-7" />
        </div>
        <h2 className="font-display text-2xl font-semibold text-foreground">
          {t("cookie_title")}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {t("cookie_description")}
        </p>

        <div className="flex flex-col gap-3 w-full mt-2">
          <button
            onClick={acceptAll}
            className="w-full px-4 py-3 rounded-xl btn-primary text-primary-foreground font-semibold text-sm cursor-pointer"
            data-testid="cookie-accept-all"
          >
            {t("cookie_accept_all")}
          </button>
          <button
            onClick={rejectAll}
            className="w-full px-4 py-3 rounded-xl border border-border text-foreground hover:bg-secondary font-semibold transition-all text-sm cursor-pointer"
            data-testid="cookie-reject-all"
          >
            {t("cookie_reject_all")}
          </button>
          <button
            onClick={() => setShowPreferences(true)}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 text-muted-foreground hover:text-foreground font-medium transition-colors text-sm cursor-pointer"
            data-testid="cookie-preferences"
          >
            <Settings className="w-4 h-4" />
            {t("cookie_preferences")}
          </button>
        </div>
      </div>
    </div>
  );
}
